import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, BadgeCheck, BadgePercent } from "lucide-react";
import { MaskedLine } from "./Reveal";
import { scrollToId } from "../hooks/useLenis";

const HERO_IMG =
  "https://images.unsplash.com/photo-1743179401043-8dae2a160e57?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxzY2FuZGluYXZpYSUyMGhpZ2h3YXklMjBsYW5kc2NhcGUlMjBzbm93fGVufDB8fHx8MTc4ODYyOTY2OHww&ixlib=rb-4.1.0&q=85";

const ROUTES = ["UMEÅ → ÖSTERSUND", "UMEÅ → SUNDSVALL", "UMEÅ → LULEÅ"];

const METRICS = [
  { icon: BadgeCheck, value: "Fast pris", label: "Alltid i förväg" },
  { icon: BadgePercent, value: "30%", label: "RUT-avdrag privat" },
  { icon: ShieldCheck, value: "0 kr", label: "Ur egen ficka vid utplacering" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden bg-[#090d16] grain">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <motion.img
          src={HERO_IMG}
          alt="Vinterväg genom Norrland"
          className="w-full h-[115%] object-cover"
          initial={{ scale: 1.15, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#090d16]/90 via-[#090d16]/60 to-[#090d16]/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#090d16] via-transparent to-transparent" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 sm:px-8 pb-14 pt-40 text-center sm:text-left">
        <motion.p
          data-testid="hero-eyebrow"
          className="font-mono2 text-[11px] sm:text-xs uppercase tracking-[0.3em] text-green-400 mb-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.8 }}
        >
          Studentflyttarna · Umeå, Norrland
        </motion.p>

        <h1 className="font-display font-extrabold text-white tracking-tight leading-[1.02] text-[clamp(2.6rem,9vw,6.5rem)]">
          <MaskedLine load delay={0.15}>{"När studierna "}</MaskedLine>
          <MaskedLine load delay={0.3}>{"flyttar, flyttar "}</MaskedLine>
          <MaskedLine load delay={0.45}><span className="text-[#16a34a]">vi.</span></MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          className="mt-7 max-w-xl mx-auto sm:mx-0 text-base sm:text-lg leading-relaxed text-slate-300"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          Fast pris i förväg, faktura direkt till ditt universitet och 30&nbsp;% RUT-avdrag för
          privatpersoner. Vi kör sträckorna Umeå–Östersund, Umeå–Sundsvall och Umeå–Luleå.
        </motion.p>

        <motion.div
          className="mt-9 flex flex-wrap items-center justify-center sm:justify-start gap-4"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <button
            data-testid="hero-cta-quote"
            onClick={() => scrollToId("#offert")}
            className="group bg-[#16a34a] hover:bg-[#15803d] text-white font-semibold px-7 py-4 rounded-full flex items-center gap-2 transition-colors duration-300"
          >
            Få offert
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-cta-grant"
            onClick={() => scrollToId("#flyttbidrag")}
            className="text-white font-semibold px-7 py-4 rounded-full border border-white/25 hover:border-white/60 hover:bg-white/5 transition-colors duration-300"
          >
            Så funkar flyttbidraget
          </button>
        </motion.div>

        <motion.div
          data-testid="hero-routes"
          className="mt-12 flex flex-wrap justify-center sm:justify-start gap-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.15, duration: 0.9 }}
        >
          {ROUTES.map((r, i) => (
            <motion.span
              key={r}
              className="font-mono2 text-[10px] sm:text-[11px] tracking-[0.18em] text-slate-300 border border-white/15 rounded-full px-4 py-2 backdrop-blur-sm bg-white/5"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2 + i * 0.12, duration: 0.6 }}
            >
              {r}
            </motion.span>
          ))}
          <motion.span
            data-testid="hero-giftcard-chip"
            className="font-mono2 text-[10px] sm:text-[11px] tracking-[0.18em] text-white bg-[#16a34a] rounded-full px-4 py-2 flex items-center gap-2"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.6, duration: 0.6 }}
          >
            P.S. STUDENTPAKET: 1 000 KR PRESENTKORT
          </motion.span>
        </motion.div>
      </motion.div>

      <motion.div
        data-testid="hero-metrics"
        className="relative z-10 border-t border-white/10 bg-[#090d16]/60 backdrop-blur-md"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
          {METRICS.map((m) => (
            <div key={m.label} className="flex flex-col sm:flex-row items-center justify-center sm:justify-start gap-2 sm:gap-4 py-5 sm:py-6 sm:px-8 sm:first:pl-0">
              <m.icon className="w-5 h-5 text-green-400 shrink-0" />
              <div className="text-center sm:text-left">
                <div className="font-display font-extrabold text-white text-xl leading-none">{m.value}</div>
                <div className="text-slate-400 text-xs mt-1">{m.label}</div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
